import { useState, useEffect } from "react";
import { axiosInstance } from "../App";
import { useBox } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { RotateCcw, Search, Download, Printer } from "lucide-react";
import * as XLSX from "xlsx";
import { todayCR } from "../utils/dateHelpers";
import { printHtmlInWindow } from "../utils/printHelper";

/**
 * CreditNotesReport — Reporte de notas de crédito por período.
 */
export default function CreditNotesReport() {
  const { selectedBox } = useBox() || {};
  const today = todayCR();
  const [startDate, setStartDate] = useState(today.slice(0, 8) + "01"); // First of month
  const [endDate, setEndDate] = useState(today);
  const [type, setType] = useState("all"); // all | full | partial
  const [q, setQ] = useState("");
  const [allBoxes, setAllBoxes] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      if (selectedBox?.id && !allBoxes) params.set("box_id", selectedBox.id);
      if (type !== "all") params.set("type", type);
      if (q.trim()) params.set("q", q.trim());
      const r = await axiosInstance.get(`/inventory/credit-notes?${params.toString()}`);
      setData(r.data);
    } catch (err) {
      toast.error("No se pudo cargar el reporte");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, [selectedBox?.id, allBoxes]);

  const printNote = async (id) => {
    try {
      const r = await axiosInstance.get(`/inventory/credit-note/print/${id}`);
      if (r.data.html) {
        const win = printHtmlInWindow(r.data.html, { width: 400, height: 600 });
        if (!win) toast.error("El navegador bloqueó la ventana");
      }
    } catch { toast.error("Error al imprimir"); }
  };

  const exportExcel = () => {
    if (!data?.credit_notes?.length) { toast.error("No hay notas para exportar"); return; }
    const rows = data.credit_notes.map((n, i) => ({
      "#": i + 1,
      Fecha: (n.created_at || "").slice(0, 10),
      Hora: (n.created_at || "").slice(11, 16),
      "Nº NC": n.document_number,
      Tipo: n.type === "partial" ? "Parcial" : "Anulación",
      "Factura Original": n.original_document_number || (n.original_sale_id || "").slice(0, 8),
      Cliente: n.customer_name || "",
      Cédula: n.customer_id || "",
      "Método Pago Original": (n.payment_method || "").toUpperCase(),
      "Items": (n.products || []).length,
      "Total ₡": Number(n.total || 0),
      Motivo: n.reason || "",
      "Creado por": n.created_by || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 4 }, { wch: 11 }, { wch: 6 }, { wch: 14 }, { wch: 10 }, { wch: 16 }, { wch: 24 }, { wch: 12 }, { wch: 14 }, { wch: 6 }, { wch: 12 }, { wch: 40 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Notas de Crédito");
    const summary = [
      ["Período:", `${startDate} → ${endDate}`],
      ["Caja:", allBoxes ? "TODAS" : (selectedBox?.name || "—")],
      ["Total NC:", data.count],
      ["Monto total ₡:", data.total],
      ["Parciales ₡:", data.by_type?.partial || 0],
      ["Anulaciones ₡:", data.by_type?.full || 0],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), "Resumen");
    XLSX.writeFile(wb, `notas_credito_${startDate}_${endDate}.xlsx`);
  };

  const fmt = (n) => `₡${Number(n || 0).toLocaleString("es-CR", { maximumFractionDigits: 2 })}`;

  return (
    <div className="container mx-auto p-4 space-y-4" data-testid="credit-notes-report">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <RotateCcw className="w-6 h-6 text-amber-600" />
          Notas de Crédito por Período
        </h1>
        <p className="text-sm text-slate-600">Historial completo de devoluciones y anulaciones</p>
      </div>

      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
            <div>
              <Label className="text-xs">Desde</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-9" data-testid="cn-start-date" />
            </div>
            <div>
              <Label className="text-xs">Hasta</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-9" data-testid="cn-end-date" />
            </div>
            <div>
              <Label className="text-xs">Tipo</Label>
              <select value={type} onChange={(e) => setType(e.target.value)} className="h-9 w-full border rounded px-2 text-sm" data-testid="cn-type-filter">
                <option value="all">Todos</option>
                <option value="partial">Devolución parcial</option>
                <option value="full">Anulación completa</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Buscar (cliente / Nº NC / motivo)</Label>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2 top-3 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-7 h-9" data-testid="cn-search" />
              </div>
            </div>
            <div className="flex gap-1">
              <Button onClick={fetchReport} disabled={loading} className="flex-1" data-testid="cn-apply-btn">
                {loading ? "..." : "Aplicar"}
              </Button>
              <Button onClick={exportExcel} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cn-export-btn">
                <Download className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="mt-2">
            <label className="text-xs flex items-center gap-1.5 text-slate-600 cursor-pointer">
              <input type="checkbox" checked={allBoxes} onChange={(e) => setAllBoxes(e.target.checked)} data-testid="cn-all-boxes" />
              Ver todas las cajas
            </label>
          </div>
        </CardContent>
      </Card>

      {data && (
        <div className="grid grid-cols-3 gap-2">
          <Card><CardContent className="pt-3 pb-3 text-center">
            <div className="text-xs text-slate-500 uppercase">Total NC</div>
            <div className="text-lg font-bold" data-testid="cn-total-count">{data.count}</div>
          </CardContent></Card>
          <Card className="bg-amber-50 border-amber-200"><CardContent className="pt-3 pb-3 text-center">
            <div className="text-xs text-amber-700 uppercase">Devoluciones Parciales</div>
            <div className="text-lg font-bold text-amber-800" data-testid="cn-partial-total">{fmt(data.by_type?.partial || 0)}</div>
          </CardContent></Card>
          <Card className="bg-red-50 border-red-200"><CardContent className="pt-3 pb-3 text-center">
            <div className="text-xs text-red-700 uppercase">Anulaciones Completas</div>
            <div className="text-lg font-bold text-red-800" data-testid="cn-full-total">{fmt(data.by_type?.full || 0)}</div>
          </CardContent></Card>
        </div>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex justify-between items-center">
            <span>Notas de Crédito del período</span>
            {data && <span className="text-xs font-normal text-slate-500">Total: {fmt(data.total)}</span>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading && <p className="text-center py-6 text-slate-500">Cargando...</p>}
          {!loading && (!data?.credit_notes?.length) && (
            <p className="text-center py-6 text-slate-500">Sin notas de crédito en el período seleccionado.</p>
          )}
          {!loading && data?.credit_notes?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cn-table">
                <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                  <tr>
                    <th className="text-left px-2 py-2">Fecha</th>
                    <th className="text-left px-2 py-2">Nº NC</th>
                    <th className="text-left px-2 py-2">Tipo</th>
                    <th className="text-left px-2 py-2">Factura</th>
                    <th className="text-left px-2 py-2">Cliente</th>
                    <th className="text-center px-2 py-2">Items</th>
                    <th className="text-right px-2 py-2">Total</th>
                    <th className="text-left px-2 py-2">Motivo</th>
                    <th className="text-center px-2 py-2">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {data.credit_notes.map((n) => (
                    <tr key={n.id} className="border-b hover:bg-amber-50/50" data-testid={`cn-row-${n.id}`}>
                      <td className="px-2 py-2 font-mono text-xs">{(n.created_at || "").slice(0, 10)}<br /><span className="text-slate-500">{(n.created_at || "").slice(11, 16)}</span></td>
                      <td className="px-2 py-2 font-mono text-xs font-semibold">{n.document_number}</td>
                      <td className="px-2 py-2">
                        {n.type === "partial" ? (
                          <span className="inline-block rounded bg-amber-100 text-amber-700 px-2 py-0.5 text-xs">Parcial</span>
                        ) : (
                          <span className="inline-block rounded bg-red-100 text-red-700 px-2 py-0.5 text-xs">Anulación</span>
                        )}
                      </td>
                      <td className="px-2 py-2 font-mono text-xs">{n.original_document_number || (n.original_sale_id || "").slice(0, 8)}</td>
                      <td className="px-2 py-2">{n.customer_name || <span className="italic text-slate-400 text-xs">—</span>}</td>
                      <td className="px-2 py-2 text-center">{(n.products || []).length}</td>
                      <td className="px-2 py-2 text-right font-semibold text-amber-700">{fmt(n.total)}</td>
                      <td className="px-2 py-2 text-xs text-slate-600 truncate max-w-xs" title={n.reason}>{n.reason || "—"}</td>
                      <td className="px-2 py-2 text-center">
                        <Button size="icon" variant="ghost" onClick={() => printNote(n.id)} className="h-7 w-7" title="Imprimir">
                          <Printer className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
